// Where the delivery details sheet is in its steps (UC-18, T7), kept while
// the sheet is closed so Close never loses what was typed. The steps belong
// to one sale: the next sale starts again from the phone.
//
// The phone is typed on the keypad. From the fifth digit the till suggests
// the saved customers whose number holds those digits, after a pause in the
// typing, and nothing on screen changes until the answer comes. A tap on one
// goes on to Where, with their addresses; "New customer" goes on to a form
// that asks for everything at once. Confirm hands the values to the order and
// saves the customer, and the sheet reopens on Where with that address.

import { currentDraft, setDelivery, type Draft } from '../draft/draft.svelte';
import { normalizePhone } from '../domain/phone';
import {
	knownCustomer,
	lookUpCustomer,
	saveCustomer,
	suggestCustomers,
	SUGGEST_FROM,
	type Suggestion,
} from './customers.svelte';
import {
	addressKey,
	freshSteps,
	type AddressForm,
	type SavedAddress,
	type StepContext,
	type Steps,
	type StepValues,
} from './deliverySteps';

// Twenty digits holds any phone number with its country code.
export const PHONE_LENGTH = 20;
// The pause in the typing before the till asks.
const SUGGEST_PAUSE_MS = 350;

// The customer Where is for: who was picked, or who was just confirmed. The
// server's answer replaces the addresses once it comes; until then, and when
// it never does, these are what the sheet shows.
interface Picked {
	/** E.164, the key the server knows them by. */
	phone: string;
	name: string;
	addresses: SavedAddress[];
	/** The address just confirmed, kept first once the server's list comes. */
	confirmed: SavedAddress | null;
}

let sale = '';
let steps = $state<Steps>(freshSteps());
let picked = $state<Picked | null>(null);
let suggested = $state<{ digits: string; list: Suggestion[] }>({ digits: '', list: [] });
// The number whose lookup has answered, so "New customer" waits for it.
let checked = $state('');
let timer: ReturnType<typeof setTimeout> | undefined;

export function normalizedOrEmpty(typed: string, countryCode: string): string {
	try {
		return typed ? normalizePhone(typed, countryCode) : '';
	} catch {
		return '';
	}
}

/** Opening the sheet: a new sale starts again, from what its draft holds. */
export function openDeliverySteps(countryCode: string): void {
	const draft = currentDraft();
	if (draft.idempotencyKey === sale) {
		// The same sale: ask again who is on Where, since a save or a
		// manager's correction may have changed them.
		if (picked) void lookUpCustomer(picked.phone);
		return;
	}
	sale = draft.idempotencyKey;
	clearTimeout(timer);
	suggested = { digits: '', list: [] };
	checked = '';
	const phone = normalizedOrEmpty(draft.customerPhone, countryCode);
	if (phone && draft.deliveryAddress.trim()) {
		// A draft read back after a reload: it was confirmed once.
		confirmed(draft, phone);
		return;
	}
	steps = freshSteps(draft.customerPhone);
	picked = null;
	ask(draft.customerPhone, countryCode);
}

function confirmed(
	values: Pick<Draft, 'customerPhone' | 'customerName'> & AddressOf,
	phone: string,
) {
	const address: SavedAddress = {
		id: '',
		address: values.deliveryAddress,
		landmark: values.deliveryLandmark,
		areaId: values.deliveryAreaId || undefined,
	};
	picked = { phone, name: values.customerName, addresses: [address], confirmed: address };
	steps = { ...freshSteps(values.customerPhone), step: 'where' };
}

type AddressOf = Pick<Draft, 'deliveryAddress' | 'deliveryLandmark' | 'deliveryAreaId'>;

/** The steps as they stand, with the picked customer's addresses. */
export function deliverySteps(): Steps {
	return { ...steps, customer: currentCustomer() };
}

/** The E.164 phone of the customer on Where, for the manager's corrections. */
export function pickedPhone(): string {
	return picked?.phone ?? '';
}

function currentCustomer(): Steps['customer'] {
	if (!picked) return null;
	const known = knownCustomer(picked.phone);
	if (!known) return { name: picked.name, addresses: picked.addresses };
	const addresses: SavedAddress[] = (known.addresses ?? []).map((a) => ({
		id: a.id,
		address: a.address,
		landmark: a.landmark,
		areaId: a.areaId,
	}));
	const kept = picked.confirmed;
	if (kept) {
		// The address just confirmed stays first, whether or not the save has
		// reached the list yet.
		// Matched as the store and the server match it: the store's copy
		// cleans an address typed with a double space (addressKey).
		const at = addresses.findIndex((a) => addressKey(a.address) === addressKey(kept.address));
		const same = at >= 0 ? addresses.splice(at, 1)[0] : undefined;
		addresses.unshift(same ? { ...same, areaId: same.areaId || kept.areaId } : kept);
	}
	return { name: known.name, addresses };
}

/** The suggestions for the digits on screen, and the digits they answer. */
export function deliverySuggestions(): { digits: string; list: Suggestion[] } {
	return suggested;
}

/** What the rules in deliverySteps need to know about the phone. */
export function stepContext(areaIds: string[], countryCode: string): StepContext {
	const normalized = normalizedOrEmpty(steps.phone, countryCode);
	return {
		areaIds,
		normalizedPhone: normalized && checked === normalized ? normalized : '',
		phoneSaved: normalized !== '' && knownCustomer(normalized) !== null,
	};
}

export function typePhone(key: string, countryCode: string): void {
	if (steps.phone.length >= PHONE_LENGTH) return;
	steps.phone += key;
	ask(steps.phone, countryCode);
}

export function deletePhone(countryCode: string): void {
	steps.phone = steps.phone.slice(0, -1);
	ask(steps.phone, countryCode);
}

// Under five digits there is nothing to ask. Otherwise the till waits for a
// pause, then asks for suggestions and, once the number reads, whether it is
// saved. An answer for digits no longer on screen is dropped, and a failure
// leaves the last list where it was.
function ask(phone: string, countryCode: string): void {
	clearTimeout(timer);
	if (phone.length < SUGGEST_FROM) {
		suggested = { digits: phone, list: [] };
		return;
	}
	timer = setTimeout(() => {
		void suggestCustomers(phone).then((list) => {
			if (list && steps.phone === phone) suggested = { digits: phone, list };
		});
		const normalized = normalizedOrEmpty(phone, countryCode);
		if (normalized) {
			void lookUpCustomer(normalized).then(() => {
				if (normalizedOrEmpty(steps.phone, countryCode) === normalized) checked = normalized;
			});
		}
	}, SUGGEST_PAUSE_MS);
}

/** A suggestion tapped: on to Where, with the address they used last. */
export function pickSuggestion(suggestion: Suggestion, countryCode: string): void {
	const last: SavedAddress[] = suggestion.address
		? [
				{
					id: suggestion.address.id,
					address: suggestion.address.address,
					landmark: suggestion.address.landmark,
					areaId: suggestion.address.areaId,
				},
			]
		: [];
	picked = { phone: suggestion.phone, name: suggestion.name, addresses: last, confirmed: null };
	steps = {
		...steps,
		step: 'where',
		phone: suggestion.national,
		pick: '',
		pickArea: '',
		addrNew: last.length === 0,
	};
	ask(suggestion.national, countryCode);
	void lookUpCustomer(suggestion.phone);
}

export function startNewCustomer(): void {
	steps.step = 'new';
}

export function startNewAddress(): void {
	steps.addrNew = true;
}

/** Back: from a new address to their addresses, from anywhere else to the phone. */
export function stepBack(): void {
	if (steps.step === 'where' && steps.addrNew && (currentCustomer()?.addresses.length ?? 0) > 0) {
		steps.addrNew = false;
		return;
	}
	steps.step = 'phone';
}

export function pickAddress(id: string): void {
	steps.pick = id;
	steps.pickArea = '';
}

export function pickAddressArea(areaId: string): void {
	steps.pickArea = areaId;
}

export function setAddressForm(patch: Partial<AddressForm>): void {
	steps.form = { ...steps.form, ...patch };
}

/** The customer on Where was deleted by a manager: back to the phone. */
export function customerGone(countryCode: string): void {
	picked = null;
	steps.step = 'phone';
	steps.addrNew = false;
	ask(steps.phone, countryCode);
}

/**
 * Confirm: the order takes the values and the customer is saved, never
 * awaited. The sheet reopens on Where with this address picked.
 */
export function confirmDelivery(values: StepValues, countryCode: string): void {
	const draft = {
		customerPhone: values.phone,
		customerName: values.name,
		deliveryAddress: values.address,
		deliveryLandmark: values.landmark,
		deliveryAreaId: values.areaId,
	};
	setDelivery(draft);
	const phone = normalizedOrEmpty(values.phone, countryCode);
	// Once saved, the till asks who this is, so Where shows the customer as
	// the server keeps them, a new one included.
	void saveCustomer({
		phone: values.phone,
		name: values.name,
		address: values.address,
		landmark: values.landmark,
		deliveryAreaId: values.areaId,
	}).then(() => (phone ? lookUpCustomer(phone) : null));
	if (phone) confirmed(draft, phone);
}
